import type { ObservableQuery, OperationVariables } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import type { WatchQueryOptions } from '@apollo/client/core/watchQueryOptions';
import equal from 'fast-deep-equal';
import type { DocumentNode } from 'graphql';
import type { MutableRefObject } from 'react';
import { useCallback, useRef, useSyncExternalStore } from 'react';
import useEffectWithoutMount from '../../util/useEffectWithoutMount';

interface QueryState<TData> {
    data: undefined | TData;
    error: undefined | Error;
    loading: boolean;
    loadingMore: boolean;
}

type FetchMoreOptions<
    TData,
    TVariables extends OperationVariables,
> = Parameters<ObservableQuery<TData, TVariables>['fetchMore']>[0];

/*
 * Loads an additional page into the currently watched query. Merging of the
 * pages is done by the field policy in the cache (or by `updateQuery`), the
 * merged result is emitted through the active subscription.
 * No-op while the query is skipped or before the first subscription.
 * */
const useFetchMore = <TData, TVariables extends OperationVariables>(
    watchedQueryRef: MutableRefObject<
        ObservableQuery<TData, TVariables> | undefined
    >,
    stateRef: MutableRefObject<QueryState<TData>>,
    notifyRef: MutableRefObject<() => void>
) => {
    return useCallback(
        async (fetchMoreOptions: FetchMoreOptions<TData, TVariables>) => {
            const watchedQuery = watchedQueryRef.current;
            if (!watchedQuery) {
                return;
            }
            stateRef.current = {
                ...stateRef.current,
                loadingMore: true,
            };
            notifyRef.current();
            let nextState: QueryState<TData>;
            try {
                await watchedQuery.fetchMore(fetchMoreOptions);
                nextState = {
                    ...stateRef.current,
                    data: watchedQuery.getCurrentResult().data,
                    loadingMore: false,
                };
            } catch (error) {
                nextState = {
                    ...stateRef.current,
                    error: error as Error,
                    loadingMore: false,
                };
            }
            // The options changed while loading - that request has been
            // aborted, its result must not overwrite the current one.
            if (watchedQueryRef.current !== watchedQuery) {
                return;
            }
            stateRef.current = nextState;
            notifyRef.current();
        },
        [watchedQueryRef, stateRef, notifyRef]
    );
};

/*
 * Apollo query hook which aborts request, if any of options are changed during different renders.
 * Please use it only if you need cancelable requests, it's not default hook.
 * Use useCachedQuery or useQuery by default. useQuery and useCachedQuery abort requests on unMount.
 * */

const useCancelableQuery = <
    TData,
    TVariables extends OperationVariables = OperationVariables,
>(
    query: DocumentNode,
    options: Omit<WatchQueryOptions<TVariables, TData>, 'query'> & {
        skip?: boolean;
    },
    isCached?: boolean
) => {
    const apolloClient = useApolloClient();

    // `onError` and `onCompleted` callback functions will not always have a
    // stable identity, so we'll exclude them from the memoization key to
    // prevent `afterExecute` from being triggered un-necessarily.
    const newOptionsForCompare = {
        ...options,
        onError: undefined,
        onCompleted: undefined,
    };

    const previousOptionsForCompareRef = useRef<typeof options | undefined>(
        undefined
    );

    if (!equal(previousOptionsForCompareRef.current, newOptionsForCompare)) {
        previousOptionsForCompareRef.current = newOptionsForCompare;
    }

    const stateRef = useRef<QueryState<TData>>({
        data: undefined,
        error: undefined,
        loading: true,
        loadingMore: false,
    });

    // The currently subscribed query and the `useSyncExternalStore` notifier,
    // so `fetchMore` can load the next page and push the result to React.
    const watchedQueryRef = useRef<
        ObservableQuery<TData, TVariables> | undefined
    >(undefined);
    const notifyRef = useRef<() => void>(() => {
        // no subscriber yet
    });

    useEffectWithoutMount(() => {
        stateRef.current = {
            data: undefined,
            error: undefined,
            loading: true,
            loadingMore: false,
        };
    }, [apolloClient]);

    const fetchMore = useFetchMore(watchedQueryRef, stateRef, notifyRef);

    const subscribe = useCallback(
        (callback: () => void) => {
            if (options.skip) {
                stateRef.current = {
                    ...stateRef.current,
                    loading: false,
                };
                return () => {
                    // skip loading
                };
            }
            stateRef.current = {
                ...stateRef.current,
                loading: true,
            };
            const watchedQuery = apolloClient.watchQuery<TData, TVariables>({
                ...options,
                query,
            });
            watchedQueryRef.current = watchedQuery;
            notifyRef.current = callback;
            if (!isCached || watchedQuery.getCurrentResult().data) {
                stateRef.current = {
                    ...stateRef.current,
                    data: watchedQuery.getCurrentResult().data,
                };
            }

            const sub = watchedQuery.subscribe({
                next: value => {
                    if (!value.partial) {
                        stateRef.current = {
                            data: value.data,
                            error: undefined,
                            loading: value.loading,
                            loadingMore: false,
                        };
                    }
                    callback();
                },
                error: error => {
                    stateRef.current = {
                        ...stateRef.current,
                        error,
                        loading: false,
                    };
                    callback();
                },
                complete: () => {
                    stateRef.current = {
                        ...stateRef.current,
                        loading: false,
                        loadingMore: false,
                    };
                    callback();
                },
            });
            return () => {
                sub.unsubscribe();
                if (watchedQueryRef.current === watchedQuery) {
                    watchedQueryRef.current = undefined;
                }
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [previousOptionsForCompareRef.current, apolloClient]
    );

    const state = useSyncExternalStore(subscribe, () => stateRef.current);
    return {
        data: state.data,
        error: state.error,
        loading: state.loading,
        fetchMore,
    };
};

export default useCancelableQuery;
